import { useMemo } from 'react'
import { load, streakInfo } from '../lib/storage'
import { isPlus } from '../lib/user'
import { emojiFor } from '../lib/scoring'
import themes from '@data/themes.json'
import { TopBar } from './TopBar'
import { BottomNav } from './BottomNav'

interface ThemeStat { id: string; name: string; emoji: string; qs: number; sharp: number; avgOrders: number }

export function Stats() {
  const plus = isPlus()
  const data = useMemo(() => {
    const s = load()
    const days = Object.values(s.results).filter((r) => !r.replay).sort((a, b) => a.date.localeCompare(b.date))
    const all = days.flatMap((d) => d.results.map((r) => ({ ...r, theme: d.theme.id })))
    const byTheme: ThemeStat[] = (themes as any[]).map((t) => {
      const qs = all.filter((r) => r.theme === t.id)
      return {
        id: t.id, name: t.name, emoji: t.emoji, qs: qs.length,
        sharp: qs.filter((r) => r.points >= 4).length,
        avgOrders: qs.length ? qs.reduce((a, r) => a + Math.log10(r.ratio), 0) / qs.length : 0,
      }
    }).filter((t) => t.qs > 0).sort((a, b) => a.avgOrders - b.avgOrders)
    const over = all.filter((r) => r.guess > r.answer * 2).length
    const under = all.filter((r) => r.guess * 2 < r.answer).length
    const trend = days.slice(-30).map((d) => ({
      date: d.date, avg: d.results.reduce((a, r) => a + Math.log10(r.ratio), 0) / d.results.length, score: d.score,
    }))
    const counts = [0, 0, 0, 0, 0, 0]; all.forEach((r) => counts[r.points]++)
    const best = [...days].sort((a, b) => b.score - a.score)[0]
    return { days, all, byTheme, over, under, trend, counts, best, streak: streakInfo(s, plus) }
  }, [plus])

  if (!plus) {
    return (
      <div className="screen">
        <TopBar />
        <h1 style={{ fontSize: 28, margin: '18px 0 4px' }}>Stats Pro ✦</h1>
        <p className="muted">Your accuracy by category, your bias, your trend over time.</p>
        <div className="card"><h3>So far</h3><div className="stat">You've played <b>{data.days.length}</b> day{data.days.length === 1 ? '' : 's'}</div><div className="tiny">Get a Plus account to unlock your extended stats — and loads more.</div></div>
        <div className="actions"><a className="btn block" href="#/plus">See what a Plus account unlocks</a></div>
      </div>
    )
  }

  const W = 400, H = 90
  const t = data.trend
  const maxAvg = Math.max(1, ...t.map((x) => x.avg))
  const pts = t.map((x, i) => [10 + (i / Math.max(1, t.length - 1)) * (W - 20), H - 12 - (x.avg / maxAvg) * (H - 24)])
  const path = pts.map((p, i) => `${i ? 'L' : 'M'} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ')
  const biasTotal = data.over + data.under

  return (
    <div className="screen">
      <TopBar />
      <h1 style={{ fontSize: 28, margin: '18px 0 4px' }}>Stats Pro ✦</h1>
      {data.days.length === 0 ? <p className="muted">Play a few days and this page comes alive.</p> : <>
      <div className="row">
        <div className="card"><h3>Days played</h3><div className="score">{data.days.length}</div></div>
        <div className="card"><h3>Streak</h3><div className="score">{data.streak.current}</div>{data.streak.frozen.length > 0 && <div className="tiny">🧊 freeze saved {data.streak.frozen[0]}</div>}</div>
      </div>
      <div className="card">
        <h3>Lifetime accuracy</h3>
        <div className="emoji-string" style={{ fontSize: 18 }}>{[5, 4, 3, 2, 1, 0].map((p) => `${emojiFor(p)}${data.counts[p]}`).join('  ')}</div>
        <div className="tiny" style={{ marginTop: 6 }}>within 1.5× on {data.counts[5] + data.counts[4]} of {data.all.length} questions ({data.all.length ? Math.round(((data.counts[5] + data.counts[4]) / data.all.length) * 100) : 0}%)</div>
      </div>
      <div className="card">
        <h3>Your bias</h3>
        {biasTotal === 0 ? <div className="stat">dead level so far</div> : <>
          <div className="stat">you {data.under >= data.over ? <>under-guess <b>{Math.round((data.under / biasTotal) * 100)}%</b></> : <>over-guess <b>{Math.round((data.over / biasTotal) * 100)}%</b></>} of the time</div>
          <div className="tiny">humans nearly always under-guess big numbers — that's the game</div>
        </>}
      </div>
      <div className="card">
        <h3>Miss trend · orders of magnitude off (lower is better)</h3>
        <svg viewBox={`0 0 ${W} ${H}`} className="curve">
          <line x1="10" x2={W - 10} y1={H - 12} y2={H - 12} stroke="rgba(242,244,255,0.15)" />
          <path d={path} fill="none" stroke="#B4FF39" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          {pts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r="3" fill="#070A14" stroke="#B4FF39" strokeWidth="2" />)}
        </svg>
        <div className="tiny">last {t.length} day{t.length === 1 ? '' : 's'} · today: {t[t.length - 1]?.avg.toFixed(1)} orders</div>
      </div>
      <div className="card">
        <h3>By category · sharpest first</h3>
        <ul className="qlist" style={{ margin: 0 }}>
          {data.byTheme.map((th) => (
            <li key={th.id}><span className="em">{th.emoji}</span><span><div>{th.name}</div><div className="p">{th.sharp}/{th.qs} sharp</div></span><span className="a">{th.avgOrders.toFixed(1)}<small>orders off</small></span></li>
          ))}
        </ul>
      </div>
      {data.best && <div className="card"><h3>Personal best</h3><div className="stat">{data.best.theme.emoji} {data.best.score.toLocaleString('en-US')}</div><div className="tiny">{data.best.date}</div></div>}
      </>}
      <BottomNav />
    </div>
  )
}
